import type {InitialSyncCompletionSnapshot} from 'src/utils/modals/InitialSyncProgressBanner'

const STORAGE_PREFIX = 'initial-sync-completion:'

export type InitialSyncCompletionRecord = {
  dismissed: boolean
  snapshot: InitialSyncCompletionSnapshot
}

const storageKey = (userId: string | number, platformId: number) =>
  `${STORAGE_PREFIX}${userId}:${platformId}`

const isProgressItem = (item: unknown): boolean => {
  if (!item || typeof item !== 'object') return false
  const row = item as Record<string, unknown>
  return (
    typeof row.display_name === 'string' &&
    typeof row.remaining_qty === 'number' &&
    typeof row.total_qty_fetched === 'number' &&
    typeof row.total_qty_available_in_channel === 'number'
  )
}

const isSnapshot = (value: unknown): value is InitialSyncCompletionSnapshot => {
  if (!value || typeof value !== 'object') return false
  const snapshot = value as Record<string, unknown>
  const platformId = snapshot.platformId
  const productsFetchedCount = snapshot.productsFetchedCount
  return (
    (platformId === null || typeof platformId === 'number') &&
    typeof snapshot.platformDisplayName === 'string' &&
    typeof snapshot.storeIdentifier === 'string' &&
    Array.isArray(snapshot.progressDetail) &&
    snapshot.progressDetail.every(isProgressItem) &&
    (productsFetchedCount === null || typeof productsFetchedCount === 'number')
  )
}

const isRecord = (value: unknown): value is InitialSyncCompletionRecord => {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return typeof record.dismissed === 'boolean' && isSnapshot(record.snapshot)
}

export const readInitialSyncCompletion = (
  userId: string | number,
  platformId: number
): InitialSyncCompletionRecord | null => {
  try {
    const raw = localStorage.getItem(storageKey(userId, platformId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!isRecord(parsed) || parsed.snapshot.platformId !== platformId) {
      try {
        localStorage.removeItem(storageKey(userId, platformId))
      } catch {
        // Ignore storage failures so logout and sync UI keep running.
      }
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const saveInitialSyncCompletion = (
  userId: string | number,
  platformId: number,
  record: InitialSyncCompletionRecord
) => {
  if (record.snapshot.platformId !== platformId) return
  try {
    localStorage.setItem(storageKey(userId, platformId), JSON.stringify(record))
  } catch {
    // Ignore quota and private-mode storage errors.
  }
}

export const removeInitialSyncCompletion = (userId: string | number, platformId: number) => {
  try {
    localStorage.removeItem(storageKey(userId, platformId))
  } catch {
    // Ignore storage failures so a new import can still clear in-memory state.
  }
}

/** Keys preserved across logout `localStorage.clear()`, same pattern as cookie consent. */
export const captureInitialSyncCompletionStorage = (): [string, string][] => {
  try {
    const entries: [string, string][] = []
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (!key?.startsWith(STORAGE_PREFIX)) continue
      const value = localStorage.getItem(key)
      if (value) entries.push([key, value])
    }
    return entries
  } catch {
    return []
  }
}

export const restoreInitialSyncCompletionStorage = (entries: [string, string][]) => {
  entries.forEach(([key, value]) => {
    if (!key.startsWith(STORAGE_PREFIX)) return
    try {
      localStorage.setItem(key, value)
    } catch {
      // Ignore quota errors so logout still finishes.
    }
  })
}
